/**
 * Internal Sales Services
 * 
 * ⚠️ WARNING: This service connects to INTERNAL backend endpoints with Basic Authentication.
 * These endpoints are hidden from public API documentation and should NOT be used
 * for standard user-facing features.
 * 
 * Endpoint: /_dummy/test_test
 * Auth: Basic Authentication (username:password)
 * Purpose: Internal sales data access
 * Use: Internal monitoring/reporting only
 */

import axios from "axios";
import { SalesApiResponse, SalesParams } from "app/types/service-types/SalesTypes";
import { toast } from "react-toastify";

const STORAGE_KEY = "internal_basic_auth";

// Create a separate axios instance for internal endpoints with Basic Auth
const language = localStorage.getItem("language") || "en";
const baseUrl = process.env.REACT_APP_API_URL;

const internalAxios = axios.create({
  baseURL: baseUrl || "https://api.dev.bemyticket.com",
  headers: {
    "Accept-Language": language,
    "Content-Type": "application/json",
  },
  withCredentials: false,
});

// Add Basic Authentication header to all requests
internalAxios.interceptors.request.use(
  (config) => {
    const basicAuth = sessionStorage.getItem(STORAGE_KEY);
    
    if (basicAuth) {
      config.headers = {
        ...(config.headers || {}),
        Authorization: `Basic ${basicAuth}`,
      };
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle authentication errors
internalAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      toast.error("Authentication failed. Please login again.");
      sessionStorage.removeItem(STORAGE_KEY);
      window.location.reload();
    }
    console.error("Internal API error:", error);
    return Promise.reject(error);
  }
);

function toDDMMYYYY(iso: string) {
  if (!iso) return iso;
  if (iso.includes("/")) return iso;
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

/**
 * Internal Sales Service - DO NOT USE FOR PUBLIC FEATURES
 * Connects to hidden backend endpoints that bypass authentication
 * Uses separate axios instance WITHOUT auth headers
 */
export const InternalSalesService = {
  /**
   * Fetch sales data from internal endpoint (no auth required)
   */
  async getSales(params: SalesParams): Promise<SalesApiResponse> {
    const { page, size, ...rest } = params;
    const query = {
      ...rest,
      fromDate: toDDMMYYYY(rest.fromDate),
      toDate: toDDMMYYYY(rest.toDate),
      page: params.page ?? 0,
      size: params.size ?? 20,
    };
    delete query.sort;
    const { data } = await internalAxios.get<SalesApiResponse>("/_dummy/test_test", { params: query });
    return data;
  },

  /**
   * Export sales as PDF from internal endpoint (no auth required)
   */
  async exportPdf(params: SalesParams) {
    const { page, size, sort, ...rest } = params;
    const query = {
      ...rest,
      fromDate: toDDMMYYYY(rest.fromDate),
      toDate: toDDMMYYYY(rest.toDate),
    };
    return internalAxios.get("/_dummy/test_test/pdf", {
      params: query,
      responseType: "arraybuffer",
    });
  },

  /**
   * Export sales as Excel from internal endpoint (no auth required)
   */
  async exportXls(params: SalesParams) {
    const { page, size, sort, ...rest } = params;
    const query = {
      ...rest,
      fromDate: toDDMMYYYY(rest.fromDate),
      toDate: toDDMMYYYY(rest.toDate),
    };
    return internalAxios.get("/_dummy/test_test/xls", {
      params: query,
      responseType: "blob",
    });
  },
};
